import React, { useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    DirectionsBus as VehicleIcon,
    Person as PersonIcon,
    PersonAdd as PersonAddIcon,
} from "@mui/icons-material";

const AssignmentCard = ({ title, icon, action, children }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2,
            borderRadius: 2.5,
            height: "100%",
            backgroundColor: "#fafafa",
        }}
    >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
                {icon}
                <Typography variant="subtitle1" fontWeight="bold">
                    {title}
                </Typography>
            </Stack>
            {action}
        </Stack>
        {children}
    </Paper>
);

const ManifestAssignmentsPanel = ({
    manifest,
    canEdit,
    onAssignEmployee,
    onAssignVehicle,
    onRemoveEmployee,
    onRemoveVehicle,
}) => {
    const [confirm, setConfirm] = useState({
        open: false,
        type: null,
        id: null,
        label: "",
    });

    const closeConfirm = () => {
        setConfirm({ open: false, type: null, id: null, label: "" });
    };

    const confirmDelete = () => {
        if (confirm.type === "employee") onRemoveEmployee(confirm.id);
        if (confirm.type === "vehicle") onRemoveVehicle(confirm.id);
        closeConfirm();
    };

    const employees = Array.isArray(manifest?.empleados) ? manifest.empleados : [];
    const vehicle = manifest?.vehiculo || null;

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <AssignmentCard
                        title="Conductores asignados"
                        icon={<PersonIcon fontSize="small" color="primary" />}
                        action={
                            canEdit ? (
                                <Button size="small" variant="outlined" onClick={onAssignEmployee}>
                                    Asignar conductor
                                </Button>
                            ) : null
                        }
                    >
                        {employees.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No hay conductores asignados.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {employees.map((emp) => (
                                    <Paper
                                        key={emp.EmpID}
                                        variant="outlined"
                                        sx={{
                                            p: 1.2,
                                            borderRadius: 2,
                                            backgroundColor: emp.IsInv ? "#fff8e1" : "#fff",
                                        }}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Box minWidth={0}>
                                                <Typography variant="body2" fontWeight="bold" noWrap>
                                                    {emp.PerNom} {emp.PerApe}
                                                </Typography>
                                                {/*<Typography variant="caption" color="text.secondary">
                                                    Usuario: {emp.usenam}
                                                </Typography>*/}
                                            </Box>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {emp.IsInv && <Chip size="small" label="Invitado" color="warning" />}
                                                {canEdit && (
                                                    <Tooltip title="Retirar conductor">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                setConfirm({
                                                                    open: true,
                                                                    type: "employee",
                                                                    id: emp.EmpID,
                                                                    label: `${emp.PerNom} ${emp.PerApe}`,
                                                                })
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </AssignmentCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <AssignmentCard
                        title="Vehículo asignado"
                        icon={<VehicleIcon fontSize="small" color="primary" />}
                        action={
                            canEdit && !manifest?.vehid ? (
                                <Button size="small" variant="outlined" onClick={onAssignVehicle}>
                                    Asignar vehículo
                                </Button>
                            ) : null
                        }
                    >
                        {!manifest?.vehid || !vehicle ? (
                            <Typography variant="body2" color="text.secondary">
                                No hay vehículo asignado.
                            </Typography>
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: manifest?.vehisinv ? "#fff8e1" : "#fff",
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {vehicle.VehMat || "Sin matrícula"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Modelo: {vehicle.VehMod || ""}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Descripción: {vehicle.VehDes || "-"}
                                        </Typography>
                                        {vehicle.VehCap && (
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                                Capacidad: {vehicle.VehCap}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {manifest?.vehisinv && <Chip size="small" label="Invitado" color="warning" />}
                                        {canEdit && (
                                            <Tooltip title="Retirar vehículo">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        setConfirm({
                                                            open: true,
                                                            type: "vehicle",
                                                            id: vehicle.VehID,
                                                            label: vehicle.VehMat || "vehículo",
                                                        })
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}
                    </AssignmentCard>
                </Grid>
            </Grid>

            <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
                <DialogTitle>Confirmar retiro</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Desea retirar {confirm.type === "employee" ? "al conductor" : "el vehículo"}{" "}
                        <strong>{confirm.label}</strong> del manifiesto?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete}>
                        Retirar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ManifestAssignmentsPanel;